import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useId,
  useMemo,
  useRef,
  useState,
} from 'react';

type SaveBarPresenceValue = {
  saveBarHeight: number;
};

type SaveBarRegistrationValue = {
  registerSaveBar: (id: string, height: number) => void;
  unregisterSaveBar: (id: string) => void;
};

const SaveBarPresenceContext = createContext<SaveBarPresenceValue>({
  saveBarHeight: 0,
});

const SaveBarRegistrationContext = createContext<
  SaveBarRegistrationValue | undefined
>(undefined);

type SaveBarPresenceProviderProps = {
  children: ReactNode;
};

export function SaveBarPresenceProvider({
  children,
}: SaveBarPresenceProviderProps) {
  const [bars, setBars] = useState<Record<string, number>>({});

  const registration = useMemo<SaveBarRegistrationValue>(
    () => ({
      registerSaveBar: (id, height) =>
        setBars((prev) =>
          prev[id] === height ? prev : { ...prev, [id]: height },
        ),
      unregisterSaveBar: (id) =>
        setBars((prev) => {
          if (!(id in prev)) return prev;
          const { [id]: _removed, ...rest } = prev;
          return rest;
        }),
    }),
    [],
  );

  const presence = useMemo<SaveBarPresenceValue>(() => {
    const heights = Object.values(bars);
    return {
      saveBarHeight: heights.length === 0 ? 0 : Math.max(...heights),
    };
  }, [bars]);

  return (
    <SaveBarRegistrationContext.Provider value={registration}>
      <SaveBarPresenceContext.Provider value={presence}>
        {children}
      </SaveBarPresenceContext.Provider>
    </SaveBarRegistrationContext.Provider>
  );
}

export function useSaveBarPresence() {
  return useContext(SaveBarPresenceContext);
}

export function useReportSaveBarRef() {
  const id = useId();
  const registration = useContext(SaveBarRegistrationContext);
  const observerRef = useRef<ResizeObserver | null>(null);

  return useCallback(
    (node: HTMLElement | null) => {
      observerRef.current?.disconnect();
      observerRef.current = null;

      if (!registration) return;

      if (!node) {
        registration.unregisterSaveBar(id);
        return;
      }

      const observer = new ResizeObserver((entries) => {
        const entry = entries[0];
        if (!entry) return;
        const height = entry.borderBoxSize?.[0]?.blockSize ?? node.offsetHeight;
        registration.registerSaveBar(id, height);
      });
      observer.observe(node, { box: 'border-box' });
      observerRef.current = observer;
      registration.registerSaveBar(id, node.getBoundingClientRect().height);
    },
    [id, registration],
  );
}
